import { useAuthStore } from '@/stores/auth'
import { getAccountGetCachedUserInfo } from '../client'
import { apiClient } from '../config/client'
import { setI18nLocale } from '@/i18n'
import { mapCultureToLocale } from '@/i18n/utils/detectLocale'

/**
 * Authentication service that can be used outside of Vue components
 * This service doesn't use Vue Query composables and can be safely called
 * from router guards or other contexts outside of setup()
 */
export class AuthService {
  private static instance: AuthService

  private constructor() {}

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  /**
   * Authenticate user using cacheKey
   * @param cacheKey - The cache key from URL parameter
   */
  async authenticateWithCacheKey(cacheKey: string): Promise<void> {
    if (!cacheKey) {
      const authStore = useAuthStore()
      authStore.setError('Cache key is required for authentication')
      throw new Error('Cache key is required for authentication')
    }

    const authStore = useAuthStore()
    authStore.setLoading(true)

    try {
      // Always use production endpoint for builds
      // During development, you can manually change this to use debug endpoint
      const response = await getAccountGetCachedUserInfo({
        query: { cacheKey },
      })

      if (!response.data) {
        throw new Error('Authentication response is empty')
      }

      // Check if the response indicates invalid authentication
      if (!response.data.isValid) {
        throw new Error(response.data.message || 'Authentication failed - invalid cache key')
      }

      // Store authentication data in Pinia store
      authStore.setAuthData({
        isValid: response.data.isValid ?? false,
        userInfo: response.data.userInfo || undefined,
        message: response.data.message,
      })

      // Set the access token in API client for subsequent requests
      if (response.data.userInfo?.accessToken) {
        apiClient.setConfig({
          headers: {
            Authorization: `Bearer ${response.data.userInfo.accessToken}`,
          },
        })
      }

      // Set locale based on user's culture from API response
      if (response.data.userInfo?.culture) {
        try {
          const targetLocale = mapCultureToLocale(response.data.userInfo.culture)
          await setI18nLocale(targetLocale)
          console.log(
            `Locale automatically set to '${targetLocale}' based on culture '${response.data.userInfo.culture}'`,
          )
        } catch (error) {
          console.warn('Failed to set locale from user culture:', error)
        }
      }

      authStore.setLoading(false)
    } catch (error) {
      authStore.setLoading(false)
      const errorMessage = error instanceof Error ? error.message : 'Unknown authentication error'
      authStore.setError(errorMessage)
      throw error
    }
  }

  /**
   * Parse cacheKey from current URL parameters
   */
  getCacheKeyFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('cacheKey')
  }

  /**
   * Initialize authentication from URL parameter
   * Should be called when the application starts
   */
  async initializeAuth(): Promise<void> {
    const cacheKey = this.getCacheKeyFromUrl()

    if (cacheKey) {
      await this.authenticateWithCacheKey(cacheKey)
    } else {
      // No cacheKey found, set error but don't redirect here
      // Let the router handle the redirect
      const authStore = useAuthStore()
      authStore.setError('No authentication cache key found in URL')
      throw new Error('No authentication cache key found in URL')
    }
  }

  /**
   * Clear authentication state
   * This will remove all user data from the store and clear API client headers
   */
  clearAuth(): void {
    const authStore = useAuthStore()
    authStore.clearAuth()

    // Remove authorization header from API client
    apiClient.setConfig({
      headers: {
        Authorization: undefined,
      },
    })
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()
